/**
 * What a filter's value is edited with.
 *
 * Extracted from the Vue application's registry rather than transcribed: every
 * filter class was read for its `getInputComponent`, following the inheritance
 * where it has none of its own, and the thirteen components that came back were
 * collapsed to the kinds below. All 91 are answered for, so a filter added on
 * the backend fails to compile here until it says what it is edited with.
 *
 * Two of those components are not a kind at all but a question passed on to the
 * field -- upstream's `SpecificFieldFilterType`, which sends `equal` on a
 * duration to a duration box and on a number to a number box, and the array
 * mixin, which asks the same of a lookup's element type. Both become
 * `'by-field'` here and are resolved by `filterInputKind`.
 */
import type { Field, FieldTypeName, ViewFilterTypeName } from '@/api/types'
import { FORMULA_TYPE_BEHAVES_AS, isArrayFormulaType } from '../fields/formula-types'
import { getFieldTimezone, type DateFieldLike } from '../fields/dates'
import { DATE_FILTER_VALUE_SEPARATOR } from './date-filters'
import { VIEW_FILTER_TYPES } from '.'
import { fieldIsCompatible, type ViewFilterTypeDef } from './types'

/** The controls a filter value can be edited with. */
export type FilterInputKind =
  /** The filter takes no value: `empty`, `not_empty`, `is_even_and_whole`. */
  | 'none'
  | 'text'
  | 'number'
  | 'duration'
  | 'rating'
  | 'boolean'
  | 'select-one'
  | 'select-many'
  | 'user'
  | 'link-row'
  | 'file-type'
  /** Operator, and a number or a date when the operator reads one. */
  | 'date'
  /** A calendar day number, stored behind a timezone. */
  | 'day-of-month'
  /**
   * One of the nineteen date filters superseded by the multi-step ones.
   *
   * They cannot be chosen -- they are hidden from the type list -- but a view
   * saved years ago still holds them, so they have to be showable. Each is a
   * multi-step filter with its operator fixed, and that operator is not
   * something the stored value says; reconstructing it would mean a second
   * table naming the operator for each of the nineteen. The value is shown as
   * the text it is instead, and the way out of one is to change the filter to
   * a type that can be chosen.
   */
  | 'legacy-date'

/** A kind, or a deferral to whatever the field is. */
type FilterInputSource = FilterInputKind | 'by-field'

export const FILTER_INPUTS = {
  equal: 'by-field',
  not_equal: 'by-field',
  contains: 'text',
  contains_not: 'text',
  contains_word: 'text',
  doesnt_contain_word: 'text',
  length_is_lower_than: 'number',
  higher_than: 'by-field',
  higher_than_or_equal: 'by-field',
  lower_than: 'by-field',
  lower_than_or_equal: 'by-field',
  is_even_and_whole: 'text',
  boolean: 'boolean',
  empty: 'text',
  not_empty: 'text',
  filename_contains: 'text',
  has_file_type: 'file-type',
  files_lower_than: 'number',
  single_select_equal: 'select-one',
  single_select_not_equal: 'select-one',
  single_select_is_any_of: 'select-many',
  single_select_is_none_of: 'select-many',
  multiple_select_has: 'select-many',
  multiple_select_has_not: 'select-many',
  multiple_collaborators_has: 'user',
  multiple_collaborators_has_not: 'user',
  user_is: 'user',
  user_is_not: 'user',
  link_row_has: 'link-row',
  link_row_has_not: 'link-row',
  link_row_contains: 'text',
  link_row_not_contains: 'text',
  has_empty_value: 'by-field',
  has_not_empty_value: 'by-field',
  has_value_equal: 'by-field',
  has_not_value_equal: 'by-field',
  has_all_values_equal: 'text',
  has_value_contains: 'by-field',
  has_not_value_contains: 'by-field',
  has_value_contains_word: 'by-field',
  has_not_value_contains_word: 'by-field',
  has_value_length_is_lower_than: 'number',
  has_any_select_option_equal: 'select-many',
  has_none_select_option_equal: 'select-many',
  has_value_higher: 'text',
  has_not_value_higher: 'text',
  has_value_higher_or_equal: 'text',
  has_not_value_higher_or_equal: 'text',
  has_value_lower: 'text',
  has_not_value_lower: 'text',
  has_value_lower_or_equal: 'text',
  has_not_value_lower_or_equal: 'text',
  date_is: 'date',
  date_is_not: 'date',
  date_is_before: 'date',
  date_is_on_or_before: 'date',
  date_is_after: 'date',
  date_is_on_or_after: 'date',
  date_is_within: 'date',
  has_date_equal: 'date',
  has_not_date_equal: 'date',
  has_date_before: 'date',
  has_not_date_before: 'date',
  has_date_on_or_before: 'date',
  has_not_date_on_or_before: 'date',
  has_date_after: 'date',
  has_not_date_after: 'date',
  has_date_on_or_after: 'date',
  has_not_date_on_or_after: 'date',
  has_date_within: 'date',
  has_not_date_within: 'date',
  date_equal: 'legacy-date',
  date_not_equal: 'legacy-date',
  date_before: 'legacy-date',
  date_before_or_equal: 'legacy-date',
  date_after: 'legacy-date',
  date_after_or_equal: 'legacy-date',
  date_after_days_ago: 'legacy-date',
  date_equals_today: 'legacy-date',
  date_before_today: 'legacy-date',
  date_after_today: 'legacy-date',
  date_equals_week: 'legacy-date',
  date_equals_month: 'legacy-date',
  date_equals_year: 'legacy-date',
  date_within_days: 'legacy-date',
  date_within_weeks: 'legacy-date',
  date_within_months: 'legacy-date',
  date_equals_days_ago: 'legacy-date',
  date_equals_months_ago: 'legacy-date',
  date_equals_years_ago: 'legacy-date',
  date_equals_day_of_month: 'day-of-month',
} satisfies Record<ViewFilterTypeName, FilterInputSource>

/**
 * The field type an input should be chosen for.
 *
 * A formula, rollup, count or lookup has no type of its own: the backend
 * computes one and reports it as `formula_type`, and for an array result the
 * entries' type as `array_formula_type`. Either way the input is the one the
 * result *behaves* like, which is the same rule the field behaviours use to
 * decide how to sort and render it.
 */
export function filterFieldType(field: Field): FieldTypeName {
  const formula = (field as { formula_type?: string }).formula_type
  if (formula === undefined) return field.type

  const resolved =
    formula === 'array'
      ? (field as { array_formula_type?: string }).array_formula_type
      : formula

  return isArrayFormulaType(resolved) ? FORMULA_TYPE_BEHAVES_AS[resolved] : 'text'
}

/**
 * The input a field type deserves when the filter defers to it.
 *
 * Everything not named here is edited as text, which is upstream's fallback
 * too: a `contains` on a phone number and on an email are the same box.
 */
const BY_FIELD: Partial<Record<FieldTypeName, FilterInputKind>> = {
  duration: 'duration',
  rating: 'rating',
  number: 'number',
  autonumber: 'number',
  boolean: 'boolean',
  single_select: 'select-one',
  multiple_select: 'select-many',
  multiple_collaborators: 'user',
}

/**
 * What this filter, on this field, is edited with.
 *
 * The valueless filters are read from the filter definition rather than listed
 * again here: `hasEditableValue` already answers it, and a second list would be
 * a second thing to keep in step.
 */
export function filterInputKind(type: ViewFilterTypeName, field: Field): FilterInputKind {
  if (VIEW_FILTER_TYPES[type].hasEditableValue === false) return 'none'

  const source = FILTER_INPUTS[type]
  if (source !== 'by-field') return source

  return BY_FIELD[filterFieldType(field)] ?? 'text'
}

/**
 * What a filter starts out holding.
 *
 * Almost always nothing, because almost every filter with an empty value has no
 * opinion and shows every row -- which is what a half-made filter should do.
 *
 * The multi-step date filters are the exception and the reason this function
 * exists. Their value is `timezone?value?operator`, and an empty operator names
 * no period at all, so a date filter created empty hides **every** row the
 * moment it is added -- on the server as well as here. It starts on "today"
 * instead: a complete filter, which somebody can then change, rather than a
 * table that appears to have emptied itself.
 */
export function defaultFilterValue(type: ViewFilterTypeName, field: Field): string {
  const kind = filterInputKind(type, field)
  const sep = DATE_FILTER_VALUE_SEPARATOR
  const timezone = getFieldTimezone(field as DateFieldLike) ?? ''

  if (kind === 'date') {
    const operator = VIEW_FILTER_TYPES[type].refusedDateOperators?.includes('today')
      ? 'nr_days_from_now'
      : 'today'
    return `${timezone}${sep}${sep}${operator}`
  }
  // The day-of-month filter carries a timezone too, and an empty number shows
  // every row -- so there is nothing to choose on its behalf.
  if (kind === 'day-of-month') return `${timezone}${sep}`

  return ''
}

/**
 * The conditions that can be chosen for a field, in the order they are offered.
 *
 * Deprecated ones are left out: they still work when a view holds them, but
 * they were superseded and offering them again would grow the number of views
 * that have to be migrated later. The order is the registry's, which is the
 * order upstream lists them in.
 */
export function filterTypesFor(field: Field): ViewFilterTypeDef[] {
  return Object.values(VIEW_FILTER_TYPES).filter(
    (definition) =>
      definition.isDeprecated !== true && fieldIsCompatible(definition, field),
  )
}

/**
 * Whether a field can be filtered on at all.
 *
 * A password field cannot: no filter declares itself compatible with one, and a
 * field offered in the list that then has no condition to choose is a dead end.
 */
export function canFilterField(field: Field): boolean {
  return filterTypesFor(field).length > 0
}

/**
 * The filter to write when a condition is decided.
 *
 * One function rather than a rule repeated at the three places a condition is
 * chosen -- adding a filter, moving it to another field, and changing the
 * condition itself. All three have to answer the same second question, "and
 * what does that condition start out holding", and answering it three times is
 * three chances to forget the date filters.
 */
export function filterFor(
  field: Field,
  type: ViewFilterTypeName,
): { field: number; type: ViewFilterTypeName; value: string } {
  return { field: field.id, type, value: defaultFilterValue(type, field) }
}
